import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@/contexts/user/types';
import { debug } from '@/utils/debug';
import { playSoundEffect, SOUNDS } from '@/utils/soundEffects';

interface BadgeUnlockResult {
  unlocked: boolean;
  badgeName?: string;
  badgeCode?: string;
}

/**
 * Hook to handle badge unlock detection and syncing
 * Badge codes must match database exactly (lowercase)
 */
export const useBadgeUnlocks = (userId: string) => {
  /**
   * Sync badges from database to ensure consistency
   */
  const syncBadgesFromDatabase = useCallback(async (): Promise<string[]> => {
    try {
      const { data, error } = await supabase
        .from('user_badges')
        .select('badge_id, badges(code, name)')
        .eq('user_id', userId);

      if (error) throw error;

      const badgeCodes = data
        .map((ub: any) => ub.badges?.code)
        .filter((code: string | undefined): code is string => !!code);

      debug.log('Synced badges from database:', badgeCodes);
      return badgeCodes;
    } catch (error) {
      debug.error('Error syncing badges:', error);
      return [];
    }
  }, [userId]);

  /**
   * Check if user should unlock a specific badge
   */
  const checkBadgeUnlock = useCallback(
    async (badgeCode: string, userData: User): Promise<BadgeUnlockResult> => {
      try {
        // Get all user's current badges to check if they already have this one
        const { data: existingBadges } = await supabase
          .from('user_badges')
          .select('badge_id, badges(code)')
          .eq('user_id', userId);

        const userBadgeCodes = existingBadges?.map((ub: any) => ub.badges?.code) || [];
        
        // If user already has this badge, skip
        if (userBadgeCodes.includes(badgeCode)) {
          return { unlocked: false };
        }

        // Get badge criteria from database
        const { data: badgeData, error: badgeError } = await supabase
          .from('badges')
          .select('*')
          .eq('code', badgeCode)
          .maybeSingle(); // Use maybeSingle to avoid error when badge doesn't exist

        if (badgeError) throw badgeError;
        
        // Badge doesn't exist in database
        if (!badgeData) {
          debug.log(`Badge ${badgeCode} not found in database - skipping`);
          return { unlocked: false };
        }

        let shouldUnlock = false;

        // Check criteria based on badge type (codes match database exactly)
        switch (badgeCode) {
          case 'first_session':
            shouldUnlock = userData.totalSessions >= 1;
            break;
          case 'five_sessions':
            shouldUnlock = userData.totalSessions >= 5;
            break;
          case 'seven_day_streak':
            shouldUnlock = userData.currentStreak >= 7;
            break;
          case 'ten_hours_lifetime':
            shouldUnlock = (userData.totalStudyHours || 0) >= 10;
            break;
          case 'ten_stars_day':
            // 10 stars in a day = 300 minutes = 5 hours
            shouldUnlock = (userData.todayStudyTime || 0) >= 300;
            break;
          case 'week_warrior':
            // Completed sessions every day for a week
            shouldUnlock = userData.currentStreak >= 7;
            break;
          case 'time_master_25':
            shouldUnlock = (userData.totalStudyHours || 0) >= 25;
            break;
          case 'top_10':
            // This would need leaderboard data - skip for now
            shouldUnlock = false;
            break;
          case 'perfect_rest_x3':
            // This would need break compliance data - skip for now
            shouldUnlock = false;
            break;
          default:
            shouldUnlock = false;
        }

        if (shouldUnlock) {
          // Award the badge
          const { error: insertError } = await supabase
            .from('user_badges')
            .insert({
              user_id: userId,
              badge_id: badgeData.id,
            });

          if (insertError) {
            // If it's a duplicate error, that's okay - badge already exists
            if (insertError.code !== '23505') {
              throw insertError;
            }
          }

          debug.log(`Badge unlocked: ${badgeData.name}`);
          playSoundEffect(SOUNDS.BADGE_UNLOCK, true);

          return {
            unlocked: true,
            badgeName: badgeData.name,
            badgeCode: badgeData.code,
          };
        }

        return { unlocked: false };
      } catch (error) {
        debug.error(`Error checking badge ${badgeCode}:`, error);
        return { unlocked: false };
      }
    },
    [userId]
  );

  /**
   * Check for all potential badge unlocks
   * Badge codes must match database exactly (lowercase)
   */
  const checkForNewBadges = useCallback(
    async (userData: User): Promise<BadgeUnlockResult[]> => {
      const badgesToCheck = [
        'first_session',
        'five_sessions',
        'seven_day_streak',
        'ten_hours_lifetime',
        'ten_stars_day',
        'week_warrior',
        'time_master_25',
      ];

      const results = await Promise.all(
        badgesToCheck.map((code) => checkBadgeUnlock(code, userData))
      );

      return results.filter((result) => result.unlocked);
    },
    [checkBadgeUnlock]
  );

  return {
    checkForNewBadges,
    syncBadgesFromDatabase,
    checkBadgeUnlock,
  };
};
